import { TURNSTILE_SCRIPT_SRC, TURNSTILE_SITE_KEY } from 'common/config/app';
import type { MountedTurnstile, TurnstileApi } from './turnstile.types';

export type { MountedTurnstile, TurnstileApi } from './turnstile.types';

export class TurnstileError extends Error {
  constructor() {
    super('Turnstile verification is unavailable');
    this.name = 'TurnstileError';
  }
}

let scriptPromise: Promise<TurnstileApi> | null = null;

export const isTurnstileEnabled = () => Boolean(TURNSTILE_SITE_KEY);

const getTurnstileApi = (): TurnstileApi | undefined =>
  (window as Window & { turnstile?: TurnstileApi }).turnstile;

const loadTurnstile = (): Promise<TurnstileApi> => {
  const turnstile = getTurnstileApi();
  if (turnstile) {
    return Promise.resolve(turnstile);
  }
  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const fail = () => {
      scriptPromise = null;
      document.querySelector(`script[src="${TURNSTILE_SCRIPT_SRC}"]`)?.remove();
      reject(new TurnstileError());
    };
    const succeed = () => {
      const loadedTurnstile = getTurnstileApi();
      if (loadedTurnstile) {
        resolve(loadedTurnstile);
      } else {
        fail();
      }
    };

    const existing = document.querySelector<HTMLScriptElement>(`script[src="${TURNSTILE_SCRIPT_SRC}"]`);
    if (existing) {
      existing.addEventListener('load', succeed);
      existing.addEventListener('error', fail);
      return;
    }

    const script = document.createElement('script');
    script.src = TURNSTILE_SCRIPT_SRC;
    script.async = true;
    script.onload = succeed;
    script.onerror = fail;
    document.head.appendChild(script);
  });

  return scriptPromise;
};

export const mountTurnstile = async (action: string, host: HTMLElement): Promise<MountedTurnstile> => {
  const turnstile = await loadTurnstile();
  host.replaceChildren();

  let token: string | null = null;
  let pendingError: Error | null = null;
  let resolveToken: ((value: string) => void) | null = null;
  let rejectToken: ((reason?: unknown) => void) | null = null;

  const widgetId = turnstile.render(host, {
    sitekey: TURNSTILE_SITE_KEY,
    action,
    appearance: 'interaction-only',
    size: 'flexible',
    theme: 'light',
    callback: value => {
      token = value;
      pendingError = null;
      resolveToken?.(value);
      resolveToken = null;
      rejectToken = null;
    },
    'error-callback': () => {
      token = null;
      pendingError = new TurnstileError();
      rejectToken?.(pendingError);
      resolveToken = null;
      rejectToken = null;
    },
  });

  const getToken = (): Promise<string> => {
    if (token) {
      const value = token;
      token = null;
      return Promise.resolve(value);
    }
    if (pendingError) {
      return Promise.reject(pendingError);
    }
    if (resolveToken) {
      return Promise.reject(new TurnstileError());
    }
    return new Promise<string>((resolve, reject) => {
      resolveToken = resolve;
      rejectToken = reject;
    });
  };

  return {
    getToken,
    reset: () => {
      token = null;
      pendingError = null;
      resolveToken = null;
      rejectToken = null;
      turnstile.reset(widgetId);
    },
    remove: () => {
      turnstile.remove(widgetId);
      host.replaceChildren();
    },
  };
};
